import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import LoadingScreen from "./components/LoadingScreen";
import SoundToggle from "./components/SoundToggle";
import WebGLFallback from "./components/WebGLFallback";
import ProgressDots from "./components/ProgressDots";
import Scene from "./three/Scene";
import GameMode from "./game/GameMode";
import Hero from "./components/sections/Hero";
import About from "./components/sections/About";
import Experience from "./components/sections/Experience";
import Projects from "./components/sections/Projects";
import Skills from "./components/sections/Skills";
import Education from "./components/sections/Education";
import Contact from "./components/sections/Contact";
import { useLenis, ScrollTrigger } from "./lib/useLenis";
import { useAmbientSound } from "./lib/useAmbientSound";
import { scrollState } from "./lib/scrollStore";
import { getDeviceQuality, supportsWebGL2 } from "./lib/deviceQuality";
import { isDesktopDevice } from "./lib/deviceMode";
import { SECTIONS } from "./data/content";

function ScrollExperience() {
  const contentRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const quality = useMemo(() => getDeviceQuality(), []);
  const lenisRef = useLenis();
  const { enabled: soundEnabled, toggle: toggleSound } = useAmbientSound();

  useEffect(() => {
    const trigger = ScrollTrigger.create({
      trigger: contentRef.current,
      start: "top top",
      end: "bottom bottom",
      scrub: true,
      onUpdate: (self) => {
        scrollState.progress = self.progress;
      },
    });

    const sectionEls = contentRef.current.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = SECTIONS.findIndex((s) => s.id === entry.target.id);
            if (idx !== -1) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.5 }
    );
    sectionEls.forEach((el) => observer.observe(el));

    return () => {
      trigger.kill();
      observer.disconnect();
    };
  }, []);

  const handleSelectSection = (index) => {
    const id = SECTIONS[index].id;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(`#${id}`, { duration: 1.4 });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="relative">
      <div className="fixed inset-0 z-0" data-testid="scene-background">
        <Scene quality={quality} />
      </div>

      <SoundToggle enabled={soundEnabled} onToggle={toggleSound} />
      <ProgressDots sections={SECTIONS} activeIndex={activeIndex} onSelect={handleSelectSection} />

      <main ref={contentRef} className="relative z-10">
        <Hero />
        <About />
        <Experience />
        <Projects />
        <Skills />
        <Education />
        <Contact />
      </main>
    </div>
  );
}

function App() {
  const [loading, setLoading] = useState(true);
  const [webglUnsupported, setWebglUnsupported] = useState(false);
  const [desktop] = useState(() => isDesktopDevice());
  const quality = useMemo(() => getDeviceQuality(), []);

  useEffect(() => {
    if (!supportsWebGL2()) {
      setWebglUnsupported(true);
    }
  }, []);

  return (
    <div className="App bg-[#050505] min-h-screen" data-testid="app-root">
      {webglUnsupported ? (
        <WebGLFallback />
      ) : (
        <>
          {loading && <LoadingScreen onComplete={() => setLoading(false)} />}
          {!loading && (desktop ? <GameMode quality={quality} /> : <ScrollExperience />)}
        </>
      )}

      <Toaster theme="dark" position="bottom-right" />
    </div>
  );
}

export default App;
